import { SequenceItem } from "../audio/Sequence.js";
import { VOEmoji, VOMelody } from "../model/DataStructure.js";
import { ButtonGroup } from "./ButtonGroup.js";
import { EmojiPlayer } from "./EmojiPlayer.js";
import { SpeedSlider } from "./SpeedSlider.js";

export interface TimelineItemOptions {
    emoji: VOEmoji;
    lineCount: number;
    lineIndex: number;
    speed: number;
    onPlayRequest?: (item: TimelineItem) => void;
    onPitchChange?: (item: TimelineItem) => void;
    onSpeedChange?: (item: TimelineItem) => void;
    onRemoveRequest?: (item: TimelineItem) => void;
    onDragStart?: (item: TimelineItem, actor: e5.Click) => void;
}

export class TimelineItem implements SequenceItem {

    public onRemoveRequest = e5.Signal.create<[TimelineItem]>();
    public onPlayRequest = e5.Signal.create<[TimelineItem]>();
    public onSpeedChange = e5.Signal.create<[TimelineItem]>();
    public onPitchChange = e5.Signal.create<[TimelineItem]>();
    public onDragStart = e5.Signal.create<[TimelineItem, e5.Click]>();

    public element: HTMLElement;
    public emoji: VOEmoji;
    public melody: VOMelody;
    public uid: number;

    private _emojiPlayer: EmojiPlayer;
    private _actor: e5.Click;
    private _slideActor: e5.Click;
    private _lineIndex: number = 0;
    private _disposables: { dispose(): void; }[] = [];
    private _steps: number;
    // private _speedSlider: SpeedSlider;
    private _speedGroup: ButtonGroup<number>;

    private static _uid: number = 1;

    constructor(options: TimelineItemOptions) {
        options = e5.object.merge({
            speed: 1
        }, options);

        this.uid = TimelineItem._uid++;
        this.melody = options.emoji.melody;

        if (options.lineCount % 2 !== 1) {
            throw new Error("The steps for a TimelineItems needs to be odd not even!");
        }

        this._steps = options.lineCount;

        this.element = document.createElement("div");
        this.element.className = "timeline-item";
        this.element.innerHTML = `
        <div class="timeline-item__header"></div>
        <div class="timeline-item__body">
            <div class="timeline-item__arrow timeline-item__arrow--top fal fa-caret-up"></div>
            <div class="timeline-item__arrow timeline-item__arrow--bottom fal fa-caret-down"></div>
            <div class="timeline-item__track">
                <div class="timeline-item__lottie" data-action="slide"></div>
            </div>
        </div>        
        <div class="timeline-item__footer">
            <div class="timeline-item__triangle"></div>
            <div class="timeline-item__button-list">
                <button class="emoji-selector-item__button" data-action="play">
                    <i class="emoji-selector-item__button-icon emoji-selector-item__button-icon--inactive fal fa-play"></i>
                    <i class="emoji-selector-item__button-icon emoji-selector-item__button-icon--active fal fa-repeat"></i>
                </button>            
            </div>
            <div class="timeline-item__speed-slider"></div>
        </div>
        `;

        this._emojiPlayer = new EmojiPlayer({
            container: this.element.querySelector(".timeline-item__lottie"),
            lottieData: options.emoji.lottieData,
            duration: "auto",//options.emoji.melody.duration
        });

        this.emoji = options.emoji;

        this._disposables.push(e5.event.on({
            type: "click",
            scope: this.element,
            selector: `[data-action="remove"]`,
            listener: (e) => {
                this.onRemoveRequest.dispatch(this);
            }
        }));

        this._disposables.push(e5.event.on({
            type: "click",
            scope: this.element,
            selector: `[data-action="play"]`,
            listener: (e) => {
                this.onPlayRequest.dispatch(this);
            }
        }));

        // this._speedSlider = new SpeedSlider({
        //     container: this.element.querySelector(".timeline-item__speed-slider"),
        //     onChange: () => {
        //         this.onSpeedChange.dispatch(this);
        //     }
        // });

        this._speedGroup = new ButtonGroup({
            container: this.element.querySelector(".timeline-item__speed-slider"),
            choices: [
                { id: 0, value: 0.8, icon: "fal fa-turtle" },
                { id: 1, value: 1, label: "1x" },
                { id: 2, value: 1.2, icon: "fal fa-rabbit-running" },
            ],
            value: 1,
            onChange: () => {
                this.onSpeedChange.dispatch(this);
            }
        });

        this.speed = options.speed;

        if (typeof options.onPlayRequest === "function") {
            this.onPlayRequest.add(options.onPlayRequest);
        }

        if (typeof options.onRemoveRequest === "function") {
            this.onRemoveRequest.add(options.onRemoveRequest);
        }

        if (typeof options.onDragStart === "function") {
            this.onDragStart.add(options.onDragStart);
        }

        if (typeof options.onPitchChange === "function") {
            this.onPitchChange.add(options.onPitchChange);
        }

        if (typeof options.onSpeedChange === "function") {
            this.onSpeedChange.add(options.onSpeedChange);
        }

        const lottie = this.element.querySelector(`.timeline-item__lottie`) as HTMLElement;
        this._actor = new e5.Click(lottie);
        this._actor.preventDefault = true;

        this._actor.onStart.add(() => {
            this.onDragStart.dispatch(this, this._actor);
        });

        // let pivot: e5.IPoint = null;
        // this._actor.onStart.add((i, a) => {
        //     pivot = e5.Transform.toLocal(lottie, this._actor.pointer.x, this._actor.pointer.y);
        //     lottie.classList.toggle("timeline-item__lottie--dragging", true);
        //     this._actor.onMove.dispatch(i, a);
        // });
        // this._actor.onMove.add(() => {
        //     let local = e5.Transform.toLocal(lottie.parentElement, this._actor.pointer.x, this._actor.pointer.y);
        //     local.x -= pivot.x;
        //     local.y -= pivot.y;
        //     lottie.style.transform = `translate(${local.x}px,${local.y}px)`;
        //     if (this._actor.pointer.distance > 25) {
        //         this.onDragStart.dispatch(this, this._actor);
        //     }
        // });
        // this._actor.onClear.add(() => {
        //     lottie.style.transform = null;
        //     lottie.classList.toggle("timeline-item__lottie--dragging", false);
        // });



        this.lineIndex = options.lineIndex;
    }

    public get playing(): boolean {
        return this.element.classList.contains("timeline-item--playing");
    }

    public set playing(value: boolean) {
        if (this.playing === value) {
            return;
        }
        this.element.classList.toggle("timeline-item--playing", value);
        this.element.querySelector(".emoji-selector-item__button").classList.toggle("emoji-selector-item__button--active", value);
    }

    // public get playing(): boolean {
    //     return this._emojiPlayer.playing;
    // }

    // public set playing(value: boolean) {
    //     if (this._emojiPlayer.playing == value) {
    //         return;
    //     }
    //     this._emojiPlayer.playing = value;
    // }

    public focus(): void {
        this.element.focus({
            preventScroll: false
        });
    }

    public get grayscale(): boolean {
        return this._emojiPlayer.grayscale;
    }

    public set grayscale(value: boolean) {
        this._emojiPlayer.grayscale = value;
    }

    public progress(ratio: number): void {
        ratio = e5.Calc.clamp(ratio, 0, 1);
        this._emojiPlayer.seek(ratio);
    }

    public get pitch(): number {
        let ratio = e5.Calc.clamp((this._lineIndex / (this._steps - 1)), 0, 1);
        ratio -= 0.5;//-0.5 to +0.5
        ratio *= -2;//+1 to -1
        return Math.round(ratio * 4);//+4 to -4
    }

    // public get speed(): number {
    //     return this._speedSlider.speed;
    // }

    // public set speed(value: number) {
    //     this._speedSlider.speed = value;
    // }

    public get speed(): number {
        return this._speedGroup.value;
    }

    public set speed(value: number) {
        this._speedGroup.value = value;
    }

    public get lineIndex(): number {
        return this._lineIndex;
    }

    public set lineIndex(value: number) {
        value = e5.Calc.clamp(Math.round(value), 0, this._steps - 1);
        if (this._lineIndex == value) {
            return;
        }
        this._lineIndex = value;

        let e: HTMLElement = this.element.querySelector(`.timeline-item__lottie`);
        e.style.top = (value / (this._steps - 1)) * 100 + "%";

        this.element.classList.toggle("timeline-item--sliding-top", value <= 0);
        this.element.classList.toggle("timeline-item--sliding-bottom", value >= this._steps - 1);
    }

    public get dragging(): boolean {
        return this.element.classList.contains("timeline-item--dragging");
    }

    public set dragging(value: boolean) {
        this.element.classList.toggle("timeline-item--dragging", value);
    }

    public get sliding(): boolean {
        return this.element.classList.contains("timeline-item--sliding");
    }

    public set sliding(value: boolean) {
        this.element.classList.toggle("timeline-item--sliding", value);
    }

    public remove(): void {
        this.element.classList.add("timeline-item--removed");
        setTimeout(() => {
            this.dispose();
        }, 500);
    }

    public dispose(): void {
        this.element.remove();
        this.onRemoveRequest.dispose();
        this.onDragStart.dispose();

        // this._speedSlider.dispose();
        this._speedGroup.dispose();

        this.onPlayRequest.dispose();
        this._emojiPlayer.dispose();
        this._actor.dispose();
        if (this._slideActor) {
            this._slideActor.dispose();
        }
        for (let d of this._disposables) {
            d.dispose();
        }
        this._disposables = null;

        // this._speedSlider = null;

        this._speedGroup = null;

        this.element = null;
        this.onPlayRequest = null;
        this.onRemoveRequest = null;
        this.onDragStart = null;
        this._emojiPlayer = null;
        this._actor = null;
        this._slideActor = null;
    }
}